import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Save, ClipboardCheck } from 'lucide-react';
import { ensureProtocolForCourse, upsertCourseProtocol } from '../../lib/course-protocols';
import type { CourseProtocol } from '../../types/course-protocols';
import { toast } from '../ui/Toaster';
import { Spinner } from '../ui/Spinner';

interface Props {
  open:        boolean;
  onClose:     () => void;
  courseId:    string | null;
  courseName?: string;
}

/**
 * Editor del Protocolo BIM del curso — usado por admin y docente. Modal
 * con todos los campos editables.
 *
 * Al abrir hace `ensureProtocolForCourse` que auto-completa fechas desde
 * los módulos si todavía no existe el protocolo. Después el usuario puede
 * ajustar lo que quiera.
 */
export function EditCourseProtocolModal({ open, onClose, courseId, courseName }: Props) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [p, setP] = useState<CourseProtocol | null>(null);

  useEffect(() => {
    if (!open || !courseId) return;
    setLoading(true);
    (async () => {
      const prot = await ensureProtocolForCourse(courseId);
      setP(prot);
      setLoading(false);
    })();
  }, [open, courseId]);

  // Cerrar con Escape + lock scroll
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const update = <K extends keyof CourseProtocol>(k: K, v: CourseProtocol[K]) =>
    setP((cur) => (cur ? { ...cur, [k]: v } : cur));

  const updateVersion = (k: 'revit' | 'formit' | 'naviswork', v: string) =>
    setP((cur) => (cur ? { ...cur, versiones: { ...cur.versiones, [k]: v } } : cur));

  const handleSave = async () => {
    if (!courseId || !p) return;
    setSaving(true);
    const updated = await upsertCourseProtocol(courseId, {
      numero_diplomado:    p.numero_diplomado,
      fecha_inicio:        p.fecha_inicio,
      fecha_entrega_1:     p.fecha_entrega_1,
      fecha_entrega_2:     p.fecha_entrega_2,
      fecha_entrega_3:     p.fecha_entrega_3,
      fecha_entrega_final: p.fecha_entrega_final,
      link_acc:            p.link_acc,
      versiones:           p.versiones,
      notas:               p.notas,
    });
    setSaving(false);
    if (updated) {
      toast.success('Protocolo BIM guardado');
      onClose();
    } else {
      toast.error('No pude guardar el protocolo');
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="glass-elevated rounded-2xl border border-white/[0.08] w-full max-w-3xl my-8">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-cubus-lime/10 border border-cubus-lime/30 flex items-center justify-center">
              <ClipboardCheck className="w-5 h-5 text-cubus-lime" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Protocolo BIM</h2>
              {courseName && <p className="text-xs text-muted">{courseName}</p>}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/[0.05] text-white/70 hover:text-white transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          {loading || !p ? (
            <div className="flex items-center justify-center py-12"><Spinner /></div>
          ) : (
            <>
              {/* Identificación */}
              <Section title="Identificación">
                <Field label="Número de diplomado" placeholder="010">
                  <input
                    type="text"
                    value={p.numero_diplomado ?? ''}
                    onChange={(e) => update('numero_diplomado', e.target.value || null)}
                    placeholder="010"
                    className={inputCls}
                  />
                </Field>
              </Section>

              {/* Fechas */}
              <Section title="Fechas">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Inicio de clases">
                    <input
                      type="date"
                      value={p.fecha_inicio ?? ''}
                      onChange={(e) => update('fecha_inicio', e.target.value || null)}
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Entrega 1">
                    <input
                      type="date"
                      value={p.fecha_entrega_1 ?? ''}
                      onChange={(e) => update('fecha_entrega_1', e.target.value || null)}
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Entrega 2">
                    <input
                      type="date"
                      value={p.fecha_entrega_2 ?? ''}
                      onChange={(e) => update('fecha_entrega_2', e.target.value || null)}
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Entrega 3">
                    <input
                      type="date"
                      value={p.fecha_entrega_3 ?? ''}
                      onChange={(e) => update('fecha_entrega_3', e.target.value || null)}
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Entrega Final">
                    <input
                      type="date"
                      value={p.fecha_entrega_final ?? ''}
                      onChange={(e) => update('fecha_entrega_final', e.target.value || null)}
                      className={inputCls}
                    />
                  </Field>
                </div>
              </Section>

              {/* Link ACC + Versiones */}
              <Section title="Recursos">
                <Field label="Link Autodesk Construction Cloud (ACC)">
                  <input
                    type="url"
                    value={p.link_acc ?? ''}
                    onChange={(e) => update('link_acc', e.target.value || null)}
                    placeholder="https://docs.b360.autodesk.com/projects/..."
                    className={inputCls}
                  />
                </Field>
                <p className="text-xs text-muted mt-1">
                  Este link se usa tanto en el protocolo como en el item ACC de "Links Útiles" del alumno.
                </p>
              </Section>

              <Section title="Versiones de software">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Field label="Revit">
                    <input
                      type="text"
                      value={p.versiones.revit ?? ''}
                      onChange={(e) => updateVersion('revit', e.target.value)}
                      placeholder="2025"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Formit">
                    <input
                      type="text"
                      value={p.versiones.formit ?? ''}
                      onChange={(e) => updateVersion('formit', e.target.value)}
                      placeholder="2025"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Naviswork">
                    <input
                      type="text"
                      value={p.versiones.naviswork ?? ''}
                      onChange={(e) => updateVersion('naviswork', e.target.value)}
                      placeholder="2025"
                      className={inputCls}
                    />
                  </Field>
                </div>
              </Section>

              <Section title="Notas adicionales">
                <textarea
                  value={p.notas ?? ''}
                  onChange={(e) => update('notas', e.target.value || null)}
                  placeholder="Cualquier instrucción específica del curso…"
                  rows={3}
                  className={inputCls + ' resize-y'}
                />
              </Section>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-white/[0.06]">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-white/80 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading || !p}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cubus-lime text-cubus-bg font-semibold hover:bg-cubus-lime-dim transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

const inputCls = 'w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.08] focus:border-cubus-lime/40 text-white text-sm focus:outline-none';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-[10px] uppercase tracking-[2px] text-cubus-lime font-semibold mb-2">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, children, placeholder }: { label: string; children: React.ReactNode; placeholder?: string }) {
  return (
    <label className="block">
      <span className="text-xs text-white/60 block mb-1">{label}</span>
      {children}
      {placeholder && <span className="text-[10px] text-white/30 block mt-0.5">{placeholder}</span>}
    </label>
  );
}
